import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ITimelapseFrame,
  resolveTimelapseFrameSrc,
} from '../services/svidashboard-image.util';

export const TIMELAPSE_FRAME_INTERVAL_MS = 1500;

export type TimelapsePlaybackPhase =
  | 'idle'
  | 'preloading'
  | 'playing'
  | 'manual'
  | 'error';

export interface ILoadedTimelapseFrame extends ITimelapseFrame {
  src: string;
  loadError: boolean;
}

interface IUseTimelapsePreloadOptions {
  frames: ITimelapseFrame[];
  objectId: string;
  enabled: boolean;
}

interface IUseTimelapsePreloadResult {
  phase: TimelapsePlaybackPhase;
  progressPercent: number;
  loadedCount: number;
  totalCount: number;
  playableFrames: ILoadedTimelapseFrame[];
  currentIndex: number;
  isPlaying: boolean;
  playbackStopped: boolean;
  errorMessage: string | null;
  handlePrevious: () => void;
  handleNext: () => void;
}

function wrapIndex(index: number, length: number): number {
  if (length === 0) {
    return 0;
  }
  return ((index % length) + length) % length;
}

function preloadImage(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
}

export function useTimelapsePreload({
  frames,
  objectId,
  enabled,
}: IUseTimelapsePreloadOptions): IUseTimelapsePreloadResult {
  const [phase, setPhase] = useState<TimelapsePlaybackPhase>('idle');
  const [progressPercent, setProgressPercent] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [playableFrames, setPlayableFrames] = useState<ILoadedTimelapseFrame[]>(
    [],
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackStopped, setPlaybackStopped] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const intervalRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);

  const clearPlaybackInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    clearPlaybackInterval();
    setPhase('idle');
    setProgressPercent(0);
    setLoadedCount(0);
    setTotalCount(0);
    setPlayableFrames([]);
    setCurrentIndex(0);
    setIsPlaying(false);
    setPlaybackStopped(false);
    setErrorMessage(null);
  }, [clearPlaybackInterval]);

  const stopPlayback = useCallback(() => {
    clearPlaybackInterval();
    setIsPlaying(false);
    setPlaybackStopped(true);
    setPhase('manual');
  }, [clearPlaybackInterval]);

  const handleManualStep = useCallback(
    (delta: number) => {
      stopPlayback();
      setCurrentIndex((prev) => wrapIndex(prev + delta, playableFrames.length));
    },
    [playableFrames.length, stopPlayback],
  );

  const handlePrevious = useCallback(() => {
    handleManualStep(-1);
  }, [handleManualStep]);

  const handleNext = useCallback(() => {
    handleManualStep(1);
  }, [handleManualStep]);

  useEffect(() => {
    if (!enabled) {
      cancelledRef.current = true;
      resetState();
      return;
    }

    if (!frames.length) {
      resetState();
      return;
    }

    cancelledRef.current = false;
    clearPlaybackInterval();
    setPhase('preloading');
    setProgressPercent(0);
    setLoadedCount(0);
    setTotalCount(frames.length);
    setPlayableFrames([]);
    setCurrentIndex(0);
    setIsPlaying(false);
    setPlaybackStopped(false);
    setErrorMessage(null);

    let completedCount = 0;
    const loadedResults: ILoadedTimelapseFrame[] = new Array(frames.length);

    const runPreload = async () => {
      await Promise.all(
        frames.map(async (frame, index) => {
          const src = resolveTimelapseFrameSrc(frame, objectId);
          const success = await preloadImage(src);
          loadedResults[index] = {
            ...frame,
            src,
            loadError: !success,
          };

          completedCount += 1;
          if (cancelledRef.current) {
            return;
          }

          setLoadedCount(completedCount);
          setProgressPercent(
            Math.round((completedCount / frames.length) * 100),
          );
        }),
      );

      if (cancelledRef.current) {
        return;
      }

      const playable = loadedResults.filter((frame) => frame && !frame.loadError);
      setPlayableFrames(playable);

      if (playable.length === 0) {
        setPhase('error');
        setErrorMessage('No images could be loaded');
        return;
      }

      setPhase('playing');
      setIsPlaying(true);
      setCurrentIndex(0);

      intervalRef.current = window.setInterval(() => {
        if (cancelledRef.current) {
          return;
        }
        setCurrentIndex((prev) => wrapIndex(prev + 1, playable.length));
      }, TIMELAPSE_FRAME_INTERVAL_MS);
    };

    void runPreload();

    return () => {
      cancelledRef.current = true;
      clearPlaybackInterval();
    };
  }, [clearPlaybackInterval, enabled, frames, objectId, resetState]);

  useEffect(
    () => () => {
      cancelledRef.current = true;
      clearPlaybackInterval();
    },
    [clearPlaybackInterval],
  );

  return {
    phase,
    progressPercent,
    loadedCount,
    totalCount,
    playableFrames,
    currentIndex,
    isPlaying,
    playbackStopped,
    errorMessage,
    handlePrevious,
    handleNext,
  };
}
